import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  Autocomplete,
  Avatar,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Company } from '../types';

type Props = {
  companies: Company[];
  value?: Company | null;
  onSelectCompany: (uuid: string | null) => void;
  loading: boolean;
};

const getCompanyLogoUrl = (company: Company) => company?.logo_url || '';
const getCompanyName = (company: Company) => company?.name || '';

// Componente Avatar com lazy loading para logo da company
const LazyCompanyAvatar: React.FC<{ company: Company }> = ({ company }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (avatarRef.current) {
      observer.observe(avatarRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const logoUrl = getCompanyLogoUrl(company);

  return (
    <Avatar
      ref={avatarRef}
      sx={{ width: 32, height: 32 }}
      src={isVisible && logoUrl && !imageError ? logoUrl : undefined}
      alt={company.name}
      onLoad={handleImageLoad}
      onError={handleImageError}
    >
      {company.name?.[0]?.toUpperCase()}
    </Avatar>
  );
};

const SearchBarCompany: React.FC<Props> = ({ companies, value, onSelectCompany, loading }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [companies]);

  const filterOptions = (options: Company[], { inputValue }: any) => {
    const search = inputValue.toLowerCase();
    return options.filter(option => {
      const companyName = option.name.toLowerCase();
      const companyEmail = option.email.toLowerCase();
      return companyName.includes(search) || companyEmail.includes(search);
    });
  };

  return (
    <Autocomplete
      value={value || null}
      sx={{ width: '100%' }}
      options={sortedCompanies}
      getOptionLabel={option => {
        return `${option.name} | ${option.email}`;
      }}
      filterOptions={filterOptions}
      onChange={(_, value) => onSelectCompany(value ? value.uuid : null)}
      loading={loading}
      noOptionsText="Nenhuma empresa encontrada."
      renderOption={(props, option) => (
        <ListItem {...props} key={option.uuid} alignItems="flex-start" sx={{ gap: 0, padding: 0 }}>
          <ListItemAvatar>
            <LazyCompanyAvatar company={option} />
          </ListItemAvatar>
          <ListItemText
            primary={option.name}
            secondary={option.email}
            primaryTypographyProps={{ fontWeight: 500, fontSize: 10 }}
            secondaryTypographyProps={{ color: 'text.secondary', fontSize: 8 }}
          />
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label="Empresa"
          variant="outlined"
          sx={{ width: isMobile ? '100%' : '40%' }}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment && <span>{params.InputProps.endAdornment}</span>}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchBarCompany;
